import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { TaskForAsyncTaskFunction } from 'ember-concurrency';

export interface StepActionBase {
  label: string;
  disabled?: boolean;
}

export interface StepActionTask extends StepActionBase {
  isTask: true;
  action: TaskForAsyncTaskFunction<unknown, () => Promise<void>>;
}

export interface StepActionFunction extends StepActionBase {
  isTask?: false;
  action: (() => void) | (() => Promise<void>);
}

export type StepAction = StepActionTask | StepActionFunction;

export interface StepActions {
  primary?: StepAction;
  secondary?: StepAction;
}

export interface StepConfig {
  id: string;
  label: string | (() => string);
  component: string | (() => string);
  validation?: () => boolean | Promise<boolean>;
  canNavigate?: () => boolean;
}

export interface StepContext {
  currentStep: string;
  stepData: Record<string, unknown>;
  setStepData: (key: string, value: unknown) => void;
  navigateToStep: (stepId: string) => void;
  canNavigateToStep: (stepId: string) => boolean;
  registerActions: (actions: StepActions) => void;
  unregisterActions: () => void;
}

interface MultiStepModalArgs {
  steps: StepConfig[];
  initialStep?: string;
  onClose: () => void;
  stepData?: Record<string, unknown>;
}

export default class MultiStepModalComponent extends Component<MultiStepModalArgs> {
  @tracked currentStepId: string;
  @tracked stepData: Record<string, unknown> = {};
  @tracked registeredActions: StepActions = {};

  constructor(owner: unknown, args: MultiStepModalArgs) {
    super(owner, args);

    this.stepData = { ...args.stepData };
    this.currentStepId = args.initialStep || this.args.steps[0]?.id || '';
  }

  get currentStep(): StepConfig | undefined {
    return this.args.steps.find((step) => step.id === this.currentStepId);
  }

  get currentStepIndex(): number {
    return this.args.steps.findIndex((step) => step.id === this.currentStepId);
  }

  get currentStepLabel(): string {
    const step = this.currentStep;
    if (!step) return '';

    return typeof step.label === 'function' ? step.label() : step.label;
  }

  get isBackEnabled(): boolean {
    return this.currentStepIndex > 0;
  }

  get isBackDisabled(): boolean {
    return this.isAnyTaskRunning;
  }

  get isPrimaryActionRunning(): boolean {
    const primaryAction = this.primaryAction;
    return !!(primaryAction && primaryAction.isTask && primaryAction.action.isRunning);
  }

  get isSecondaryActionRunning(): boolean {
    const secondaryAction = this.secondaryAction;
    return !!(secondaryAction && secondaryAction.isTask && secondaryAction.action.isRunning);
  }

  get isAnyTaskRunning(): boolean {
    return this.isPrimaryActionRunning || this.isSecondaryActionRunning;
  }

  get primaryAction(): StepAction | undefined {
    return this.registeredActions.primary;
  }

  get secondaryAction(): StepAction | undefined {
    return this.registeredActions.secondary;
  }

  get isPrimaryActionDisabled(): boolean {
    const action = this.primaryAction;
    return action?.disabled || this.isPrimaryActionRunning;
  }

  get isSecondaryActionDisabled(): boolean {
    const action = this.secondaryAction;
    return action?.disabled || this.isSecondaryActionRunning;
  }

  get shouldShowSecondaryAction(): boolean {
    return !!this.secondaryAction && !this.isAnyTaskRunning;
  }

  get stepContext(): StepContext {
    return {
      currentStep: this.currentStepId,
      stepData: this.stepData,
      setStepData: (key: string, value: unknown) => this.setStepData(key, value),
      navigateToStep: (stepId: string) => this.navigateToStep(stepId),
      canNavigateToStep: (stepId: string) => this.canNavigateToStep(stepId),
      registerActions: (actions: StepActions) => this.registerActions(actions),
      unregisterActions: () => this.unregisterActions(),
    };
  }

  @action
  setStepData(key: string, value: unknown): void {
    this.stepData = { ...this.stepData, [key]: value };
  }

  @action
  registerActions(actions: StepActions): void {
    this.registeredActions = actions;
  }

  @action
  unregisterActions(): void {
    this.registeredActions = {};
  }

  @action
  navigateToStep(stepId: string): void {
    if (!this.canNavigateToStep(stepId)) {
      return;
    }

    // Clear registered actions when navigating
    this.unregisterActions();

    // Update to target step
    this.currentStepId = stepId;
  }

  @action
  canNavigateToStep(stepId: string): boolean {
    const targetStepIndex = this.args.steps.findIndex((step) => step.id === stepId);
    const currentIndex = this.currentStepIndex;

    if (targetStepIndex === -1) return false;

    // Can always go back
    if (targetStepIndex < currentIndex) return true;

    // Validate all steps between current and target
    for (let i = currentIndex; i <= targetStepIndex; i++) {
      const step = this.args.steps[i];
      if (step?.canNavigate && !step.canNavigate()) {
        return false;
      }
    }

    return true;
  }

  @action
  async handlePrimaryAction(): Promise<void> {
    const primaryAction = this.primaryAction;
    if (!primaryAction) return;

    try {
      if (primaryAction.isTask) {
        // This is a task action
        await primaryAction.action.perform();
      } else {
        // This is a regular function action
        const result = primaryAction.action();
        if (result instanceof Promise) {
          await result;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Primary action failed:', error);
    }
  }

  @action
  async handleSecondaryAction(): Promise<void> {
    const secondaryAction = this.secondaryAction;
    if (!secondaryAction) return;

    try {
      if (secondaryAction.isTask) {
        // This is a task action
        await secondaryAction.action.perform();
      } else {
        // This is a regular function action
        const result = secondaryAction.action();
        if (result instanceof Promise) {
          await result;
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Secondary action failed:', error);
    }
  }

  @action
  handleBack(): void {
    if (!this.isBackEnabled || this.isBackDisabled) return;

    const previousStepIndex = this.currentStepIndex - 1;
    if (previousStepIndex >= 0) {
      const previousStep = this.args.steps[previousStepIndex];
      if (previousStep) {
        this.navigateToStep(previousStep.id);
      }
    }
  }

  @action
  handleContinue(): void {
    const nextStepIndex = this.currentStepIndex + 1;
    if (nextStepIndex < this.args.steps.length) {
      const nextStep = this.args.steps[nextStepIndex];
      if (nextStep) {
        this.navigateToStep(nextStep.id);
      }
    }
  }

  @action
  handleClose(): void {
    if (this.args.onClose) {
      this.args.onClose();
    }
  }

  @action
  async validateCurrentStep(): Promise<boolean> {
    const step = this.currentStep;
    if (!step?.validation) return true;

    try {
      const result = step.validation();
      return result instanceof Promise ? await result : result;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Step validation failed:', error);
      return false;
    }
  }
}
